/**
 * @file rendering-loop.ts
 * @description Isolates canvas initialization, resize logic, and the central animation/render loop.
 */
import { g } from '@hcie/core';
import { renderLayers } from './layers';
import { attachCanvasEvents } from './event-dispatcher';

export let originalCanvas: HTMLCanvasElement | null = null;
export let zoomCanvas: HTMLCanvasElement | null = null;
export let initWrapper: HTMLElement | null = null;
export let can: HTMLCanvasElement | null = null;

export let tempCanvas: HTMLCanvasElement | null = null;
export let tempCtx: CanvasRenderingContext2D | null = null;

export function initCanvasRefs() {
    originalCanvas = document.getElementById("originalCanvas") as HTMLCanvasElement;
    zoomCanvas = document.getElementById("zoomCanvas") as HTMLCanvasElement;
    
    // Initialize tempCtx immediately if tempCanvas exists
    if (tempCanvas) {
        if (!document.getElementById("tempCanvas")) {
            tempCanvas.style.display = "none";
            document.body.appendChild(tempCanvas);
        }
        tempCtx = tempCanvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
        (window as any).tempCtx = tempCtx;
        (window as any).tempCanvas = tempCanvas;
    }
    initWrapper = document.getElementById("canvasWrapper");
    can = document.getElementById("drawingCanvas") as HTMLCanvasElement;
}

export function resizeCanvas(width: number, height: number) {
    initCanvasRefs();
    if (!can) return;
    
    g.image_width = width;
    g.image_height = height;

    if (can) {
        can.width = width;
        can.height = height;
    }
    if (originalCanvas) {
        originalCanvas.width = width;
        originalCanvas.height = height;
    }

    if (!tempCanvas || tempCanvas.width !== width || tempCanvas.height !== height) {
        tempCanvas = document.createElement('canvas'); // Use standard createElement for reliability
        tempCanvas.width = width;
        tempCanvas.height = height;
        tempCanvas.id = "tempCanvas";
        tempCanvas.style.display = "none"; // Keep it hidden from layout
        
        // Append to body if not already in DOM to ensure getElementById works
        if (!document.getElementById("tempCanvas")) {
            document.body.appendChild(tempCanvas);
        }

        tempCtx = tempCanvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
        (window as any).tempCtx = tempCtx;
        (window as any).tempCanvas = tempCanvas;
    }

    if (initWrapper) {
        initWrapper.style.width = `${width}px`;
        initWrapper.style.height = `${height}px`;
    }
    if (zoomCanvas) {
        zoomCanvas.width = width * g.zoom;
        zoomCanvas.height = height * g.zoom;
    }

    if (typeof g.documents !== 'undefined' && g.activeDocumentIndex >= 0) {
        const doc = g.documents[g.activeDocumentIndex];
        if (doc) {
            doc.width = width;
            doc.height = height;
        }
    }

    console.log(`Canvas resized to ${width}x${height}`);
    if (can) attachCanvasEvents(can);
    if (typeof renderLayers === 'function') renderLayers();
}

export function animate() {
    if (!can || !document.getElementById("drawingCanvas")) {
        initCanvasRefs();
        if (can) {
            console.log('[RenderingLoop] Canvas re-attached to animation loop');
            attachCanvasEvents(can);
        }
    }
    
    if (!can) {
        requestAnimationFrame(animate);
        return;
    }

    const hasPreview = g.selectionPreviewBorder && g.selectionPreviewBorder.length > 0;
    const shouldRender = (g as any).needsRender || g.isSelectionActive || hasPreview || g.drawing;

    if (shouldRender && typeof renderLayers === 'function') {
        const live = g.drawing && tempCanvas ? tempCanvas : undefined;
        renderLayers(live);
        
        // Only clear needsRender if we aren't currently drawing
        // This ensures the NEXT animation frame also renders if g.drawing is still true
        if (!g.drawing) {
            (g as any).needsRender = false;
        }
    }
    
    requestAnimationFrame(animate);
}

// Initialize loop
if (typeof window !== 'undefined') {
    (window as any).resizeCanvas = resizeCanvas;
    (window as any).initCanvasRefs = initCanvasRefs; // expose here — event-dispatcher circular dep makes it null otherwise
    (window as any).tempCanvas = tempCanvas;
    (window as any).originalCanvas = originalCanvas;

    // Start the central animation loop
    requestAnimationFrame(animate);
}
